/**
 * Global War Effort warnings: fortified country under attack, HQ under attack,
 * opponent 40/45/50 orders-in-strategy. Shown everywhere (War Effort, BattleUI, other screens).
 * Plays alarm_wareffort.mp3 when a warning appears.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from './config/apiConfig';

const WARNING_DURATION_MS = 5000;
const POLL_INTERVAL_MS = 10000;
const OPPONENT_THRESHOLDS = [40, 45, 50];

function playWarningSound() {
  try {
    const audio = new Audio('/sounds/alarm_wareffort.mp3');
    audio.volume = 0.8;
    audio.play().catch(() => {});
  } catch (_) {}
}

function playPanicSound() {
  try {
    const audio = new Audio('/sounds/panic.mp3');
    audio.volume = 0.8;
    audio.play().catch(() => {});
  } catch (_) {}
}

export function WarEffortWarnings() {
  const { t } = useTranslation();
  const [missionId, setMissionId] = useState(null);
  const [message, setMessage] = useState(null);
  const [queue, setQueue] = useState([]);
  const timerRef = useRef(null);
  const pollCleanupRef = useRef(null);
  const retryIntervalRef = useRef(null);
  const prevFortifiedRef = useRef([]);
  const prevHqUnderAttackRef = useRef(false);
  const prevOpponentCountsRef = useRef({});
  const missionIdRef = useRef(null);

  const clearPerMissionWarningState = useCallback(() => {
    prevFortifiedRef.current = [];
    prevHqUnderAttackRef.current = false;
  }, []);

  const showNextInQueue = useCallback(() => {
    setQueue((q) => {
      const rest = q.slice(1);
      setMessage(rest[0] ?? null);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (rest.length > 0) {
        timerRef.current = setTimeout(showNextInQueue, WARNING_DURATION_MS);
      }
      return rest;
    });
  }, []);

  const enqueueAndPlay = useCallback((msg) => {
    if (!msg) return;
    playWarningSound();
    setQueue((q) => [...q, msg]);
    setMessage((m) => {
      if (m) return m;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(showNextInQueue, WARNING_DURATION_MS);
      return msg;
    });
  }, [showNextInQueue]);

  const markWarnedAttackCount = useCallback((missionId, opponentUserId, threshold) => {
    const token = localStorage.getItem('token');
    if (!token || missionId == null) return;
    fetch(`${API_URL}/war-effort/mission/${missionId}/warned-attack-count`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ opponentUserId, threshold })
    }).catch(() => {});
  }, []);

  const processStatusData = useCallback(
    (data, enqueue, opts = {}) => {
      const { missionId: mid, markWarned = () => {} } = opts;
      const fortified = Array.isArray(data.fortifiedCountriesUnderAttack) ? data.fortifiedCountriesUnderAttack : [];
      const hqUnderAttack = !!data.hqUnderAttack;
      const opponentCounts = Array.isArray(data.opponentAttackCounts) ? data.opponentAttackCounts : [];
      const warnedByServer = data.warnedAttackCountThresholds || {};

      const prevCellIds = new Set((prevFortifiedRef.current || []).map((x) => x.countryCellId));
      for (const x of fortified) {
        if (prevCellIds.has(x.countryCellId)) continue;
        const name = x.countryName || `Cell ${x.countryCellId}`;
        enqueue(t('warEffort.fortifiedCountryUnderAttackSingle', { country: name }));
      }
      prevFortifiedRef.current = fortified;

      if (hqUnderAttack && !prevHqUnderAttackRef.current) {
        prevHqUnderAttackRef.current = true;
        enqueue(t('warEffort.hqUnderAttack'));
      } else if (!hqUnderAttack) {
        prevHqUnderAttackRef.current = false;
      }

      for (const { userId, username, count } of opponentCounts) {
        const alreadyWarned = warnedByServer[userId] || [];
        for (const thresh of OPPONENT_THRESHOLDS) {
          if (count >= thresh && !alreadyWarned.includes(thresh)) {
            const msg = t(`warEffort.opponentAttackCards${thresh}`, { username: username || `Player ${userId}` });
            enqueue(msg);
            playPanicSound();
            markWarned(mid, userId, thresh);
          }
        }
      }
      prevOpponentCountsRef.current = opponentCounts;
    },
    [t]
  );

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    let mounted = true;

    const fetchActiveMission = async () => {
      try {
        const res = await fetch(`${API_URL}/war-effort/active-mission`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const mission = await res.json();
          if (mission && mission.id && mission.status !== 'abandoned') {
            return mission.id;
          }
        }
        return null;
      } catch (_) {
        return null;
      }
    };

    const startPolling = (mid) => {
      const poll = async () => {
        if (!mounted) return;
        try {
          const statusRes = await fetch(`${API_URL}/war-effort/mission-status/${mid}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!statusRes.ok || !mounted) return;
          const data = await statusRes.json();
          processStatusData(data, enqueueAndPlay, { missionId: mid, markWarned: markWarnedAttackCount });
        } catch (_) {}
      };
      poll();
      const intervalId = setInterval(poll, POLL_INTERVAL_MS);
      pollCleanupRef.current = () => clearInterval(intervalId);
    };

    (async () => {
      let mid = await fetchActiveMission();
      if (!mounted) return;
      if (mid) {
        clearPerMissionWarningState();
        missionIdRef.current = mid;
        setMissionId(mid);
        startPolling(mid);
      }
      retryIntervalRef.current = setInterval(async () => {
        if (!mounted) return;
        mid = await fetchActiveMission();
        if (!mounted) return;
        if (mid) {
          clearPerMissionWarningState();
          missionIdRef.current = mid;
          setMissionId(mid);
          startPolling(mid);
          if (retryIntervalRef.current) {
            clearInterval(retryIntervalRef.current);
            retryIntervalRef.current = null;
          }
        }
      }, 30000);
    })();

    return () => {
      missionIdRef.current = null;
      mounted = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (pollCleanupRef.current) {
        pollCleanupRef.current();
        pollCleanupRef.current = null;
      }
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
    };
  }, [t, enqueueAndPlay, processStatusData, clearPerMissionWarningState, markWarnedAttackCount]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const handler = async (e) => {
      const mid = e.detail?.mission?.id ?? missionIdRef.current;
      if (mid == null) return;
      try {
        const statusRes = await fetch(`${API_URL}/war-effort/mission-status/${mid}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!statusRes.ok) return;
        const data = await statusRes.json();
        processStatusData(data, enqueueAndPlay, { missionId: mid, markWarned: markWarnedAttackCount });
      } catch (_) {}
    };

    window.addEventListener('war-effort-mission-update', handler);
    return () => window.removeEventListener('war-effort-mission-update', handler);
  }, [processStatusData, enqueueAndPlay, markWarnedAttackCount]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (!message || typeof document === 'undefined') return null;

  const overlay = createPortal(
    <div
      className="war-effort-global-warning"
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 100001,
        padding: '24px 48px',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        border: '4px solid #cc0000',
        borderRadius: '12px',
        color: '#fff',
        fontSize: '22px',
        fontWeight: 'bold',
        textAlign: 'center',
        boxShadow: '0 0 40px rgba(204, 0, 0, 0.6)',
        pointerEvents: 'none'
      }}
    >
      {message}
    </div>,
    document.body
  );

  return overlay;
}

export default WarEffortWarnings;
